// opr-cards
import { onDestroy, onMount } from "@/modules/_";
import gsap, { Draggable, reduced } from "@lib/gsap";

const OFFSET_PX = 40; // horizontal stagger of the upcoming stack (also the incoming card's slide-in travel)
const SCALE_STEP = 0.01;
const Y_STEP_PX = 0;
const ROT_STEP_DEG = 1.2;
const MAX_BEHIND = 4;
const EXIT_STEP_PX = 220; // how far a passed card slides off to the side (per step) as it fades
const TWEEN_DURATION = 0.7;
const TWEEN_EASE = "elastic.out(1, 0.75)";
const RELEASE_EASE = "power3.out";
const DRAG_THRESHOLD = 0.15;
const ACTIVE_DOT_CLASS = "is--active";
const ACTIVE_CARD_CLASS = "is--active";

export default function (element: HTMLElement) {
  const cards = Array.from(
    element.querySelectorAll<HTMLElement>('[opr-cards="item"]'),
  );
  if (cards.length === 0) return;

  const total = cards.length;
  const dotsM = element.querySelector<HTMLElement>('[opr-cards="dots-m"]');
  const dotTemplate = element.querySelector<HTMLElement>('[opr-cards="dot"]');

  let activeIndex = 0;

  function distanceFromActive(i: number): number {
    return i - activeIndex;
  }

  function getPose(distance: number) {
    if (distance === 0) {
      return { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1, z: total + 1 };
    }
    // Passed cards slide off to the side and fade — directional "travel".
    if (distance < 0) {
      return {
        x: distance * EXIT_STEP_PX,
        y: 0,
        rotation: 0,
        scale: 1,
        opacity: 0,
        z: 0,
      };
    }
    // Far-ahead cards anchor at the deepest visible pose so they don't teleport in.
    if (distance > MAX_BEHIND) {
      const d = MAX_BEHIND;
      return {
        x: d * OFFSET_PX,
        y: d * Y_STEP_PX,
        rotation: d * ROT_STEP_DEG,
        scale: 1 - d * SCALE_STEP,
        opacity: 0,
        z: 0,
      };
    }
    return {
      x: distance * OFFSET_PX,
      y: distance * Y_STEP_PX,
      rotation: distance * ROT_STEP_DEG,
      scale: 1 - distance * SCALE_STEP,
      opacity: 1,
      z: total - distance,
    };
  }

  function poseForCard(i: number, fromActive: number) {
    return getPose(i - fromActive);
  }

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  function setupDots(): HTMLElement[] {
    if (!dotsM || !dotTemplate) return [];
    const built: HTMLElement[] = [];
    dotTemplate.classList.remove(ACTIVE_DOT_CLASS);
    dotTemplate.style.cursor = "pointer";
    built.push(dotTemplate);
    for (let i = 1; i < total; i++) {
      const clone = dotTemplate.cloneNode(true) as HTMLElement;
      clone.classList.remove(ACTIVE_DOT_CLASS);
      dotsM.appendChild(clone);
      built.push(clone);
    }
    return built;
  }

  function render(animate: boolean, dots: HTMLElement[]) {
    cards.forEach((card, i) => {
      const dist = distanceFromActive(i);
      const pose = getPose(dist);
      gsap.set(card, { zIndex: pose.z });
      card.classList.toggle(ACTIVE_CARD_CLASS, dist === 0);
      const target = {
        x: pose.x,
        y: pose.y,
        rotation: pose.rotation,
        scale: pose.scale,
        opacity: pose.opacity,
      };
      if (animate && !reduced) {
        gsap.to(card, {
          ...target,
          duration: TWEEN_DURATION,
          ease: TWEEN_EASE,
        });
      } else {
        gsap.set(card, target);
      }
    });
    dots.forEach((dot, i) => {
      dot.classList.toggle(ACTIVE_DOT_CLASS, i === activeIndex);
    });
  }

  onMount(() => {
    cards.forEach((card) => {
      gsap.set(card, {
        position: "absolute",
        top: 0,
        left: 0,
        transformOrigin: "left center",
      });
    });

    const dots = setupDots();
    render(false, dots);

    const setActive = (i: number) => {
      const next = Math.max(0, Math.min(total - 1, i));
      if (next === activeIndex) return;
      activeIndex = next;
      render(true, dots);
    };

    const dotUnsubs = dots.map((dot, i) => {
      const handler = () => setActive(i);
      dot.addEventListener("click", handler);
      return () => dot.removeEventListener("click", handler);
    });

    // Overlay covers the card stack only; dots sit outside (bottom: -1.5rem) so their clicks pass through.
    let dragger: HTMLDivElement | null = null;
    let dragInstance: Draggable | null = null;

    if (!reduced) {
      dragger = document.createElement("div");
      dragger.setAttribute("opr-cards-dragger", "");
      Object.assign(dragger.style, {
        position: "absolute",
        inset: "0",
        zIndex: "9999",
        touchAction: "pan-y",
        cursor: "grab",
        background: "transparent",
      });
      element.appendChild(dragger);

      const sliderWidth = element.offsetWidth || window.innerWidth;
      dragInstance = Draggable.create(dragger, {
        type: "x",
        edgeResistance: 0.8,
        bounds: { minX: -sliderWidth / 2, maxX: sliderWidth / 2 },
        inertia: false,
        onPress(this: Draggable) {
          (this.target as HTMLElement).style.cursor = "grabbing";
          gsap.killTweensOf(cards);
        },
        onDrag(this: Draggable) {
          if (reduced) return;
          const raw = this.x / sliderWidth;
          const progress = Math.min(1, Math.abs(raw));
          // Swipe the top card left to advance: drag-left → next, drag-right → previous.
          const direction = raw > 0 ? -1 : 1;
          const nextIndex = Math.max(
            0,
            Math.min(total - 1, activeIndex + direction),
          );

          cards.forEach((card, i) => {
            const from = poseForCard(i, activeIndex);
            const to = poseForCard(i, nextIndex);
            gsap.set(card, {
              x: lerp(from.x, to.x, progress),
              y: lerp(from.y, to.y, progress),
              rotation: lerp(from.rotation, to.rotation, progress),
              scale: lerp(from.scale, to.scale, progress),
              opacity: lerp(from.opacity, to.opacity, progress),
              zIndex: progress > 0.5 ? to.z : from.z,
            });
          });
        },
        onRelease(this: Draggable) {
          (this.target as HTMLElement).style.cursor = "grab";
          const raw = this.x / sliderWidth;
          if (raw < -DRAG_THRESHOLD) {
            activeIndex = Math.min(total - 1, activeIndex + 1); // left → next
          } else if (raw > DRAG_THRESHOLD) {
            activeIndex = Math.max(0, activeIndex - 1); // right → previous
          }
          render(true, dots);
          gsap.to(this.target, {
            x: 0,
            duration: 0.3,
            ease: RELEASE_EASE,
          });
        },
      })[0]!;
    }

    onDestroy(() => {
      dotUnsubs.forEach((unsub) => unsub());
      dragInstance?.kill();
      dragger?.remove();
      dots.slice(1).forEach((d) => d.remove());
      if (dots[0]) {
        dots[0].classList.add(ACTIVE_DOT_CLASS);
        dots[0].style.cursor = "";
      }
      cards.forEach((c) => c.classList.remove(ACTIVE_CARD_CLASS));
      gsap.killTweensOf(cards);
      gsap.set(cards, { clearProps: "all" });
    });
  });
}
